import { DateRangeOutlined } from "@mui/icons-material";
import MUIDateTimePicker, {
  DateTimePickerProps as MUIDateTimePickerProps,
} from "@mui/lab/DateTimePicker";
import { Box } from "@mui/system";
import * as React from "react";

import { DatePickerInput } from "~/src/components/common/datepicker/DatePicker";

import { MUIPickerProvider } from "../MUIPickerProvider";

const POPPER_ATTR = "data-robee-datetime-picker";

export type Props = {
  value: Date | null;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  onChange: MUIDateTimePickerProps<Date>["onChange"];
  /** Raise calendar above overlays (e.g. Chakra Modal z-index 1400). Omit for default MUI stacking. */
  popperZIndex?: number;
};

export const DateTimePicker: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({
  value,
  error,
  disabled = false,
  placeholder,
  onChange,
  popperZIndex,
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isOpen = React.useMemo(() => Boolean(anchorEl), [anchorEl]);

  const handleOpen = React.useCallback<React.MouseEventHandler<HTMLElement>>(
    (event) => {
      if (!isOpen && !disabled) setAnchorEl(event.currentTarget);
    },
    [disabled, isOpen]
  );

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Capture-phase: Chakra ModalContent stops click propagation, so bubble-phase
  // ClickAwayListener never fires for clicks inside the modal.
  React.useEffect(() => {
    if (!isOpen) return undefined;

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (containerRef.current?.contains(target)) return;
      if (target.closest(`[${POPPER_ATTR}]`)) return;
      handleClose();
    };

    document.addEventListener("mousedown", onMouseDown, true);
    return () => document.removeEventListener("mousedown", onMouseDown, true);
  }, [handleClose, isOpen]);

  const handleRender = React.useCallback<
    MUIDateTimePickerProps<Date>["renderInput"]
  >(
    ({ inputRef, inputProps, InputProps }) => (
      <Box onClick={handleOpen}>
        <DatePickerInput
          inputRef={inputRef}
          inputProps={{
            ...inputProps,
            placeholder,
          }}
          InputProps={{ ...InputProps, disabled }}
          icon={<DateRangeOutlined />}
          error={!!error}
          errorMessage={error}
        />
      </Box>
    ),
    [disabled, error, handleOpen, placeholder]
  );

  const popperProps = React.useMemo(() => {
    const props = {
      // Custom attr for click-outside (not on PopperProps typings).
      [POPPER_ATTR]: "",
      ...(popperZIndex != null ? { style: { zIndex: popperZIndex } } : {}),
    } as NonNullable<MUIDateTimePickerProps<Date>["PopperProps"]>;
    return props;
  }, [popperZIndex]);

  return (
    <MUIPickerProvider>
      <Box ref={containerRef}>
        <MUIDateTimePicker
          value={value}
          open={isOpen}
          onChange={onChange}
          onAccept={handleClose}
          renderInput={handleRender}
          clearable
          ampm={false}
          inputFormat="yyyy/MM/dd HH:mm"
          mask="____/__/__ __:__"
          views={["year", "month", "day", "hours", "minutes"]}
          disabled={disabled}
          PopperProps={popperProps}
        />
      </Box>
    </MUIPickerProvider>
  );
};
